import type { Passage } from "./types";

export const passage: Passage = {
  slug: "romans-12-2",
  reference: "Romans 12:2",
  title: "Romans 12:2 meaning: both verbs are passive",
  h1: "Romans 12:2, where both verbs are passive",
  description:
    "Third most-read verse of 2025 on the world's largest Bible app. Do not be conformed, be transformed — and the grammar is the argument: something is shaping you either way.",
  targets: [
    "romans 12:2 meaning",
    "do not conform to this world explained",
    "renewing of your mind meaning",
  ],
  published: "2026-08-27",
  standfirst:
    "Do not be conformed. Be transformed. Neither of those is something you do to yourself, and noticing that changes what the verse is asking for.",
  verse: {
    text: "Don't be conformed to this world, but be transformed by the renewing of your mind, so that you may prove what is the good, well-pleasing, and perfect will of God.",
    ref: "Romans 12:2",
  },
  context: [
    {
      text: "Therefore I urge you, brothers, by the mercies of God, to present your bodies a living sacrifice, holy, acceptable to God, which is your spiritual service.",
      ref: "Romans 12:1",
    },
    {
      text: "For I say … to everyone who is among you, not to think of yourself more highly than you ought to think; but to think reasonably, as God has apportioned to each person a measure of faith.",
      ref: "Romans 12:3",
    },
  ],
  takeaways: [
    "Both verbs are passive: be conformed, be transformed. The choice is not whether you are shaped but by what — nobody gets the option of remaining unformed.",
    "It follows directly from verse 1, which is about bodies and ordinary life; the transformation described is not primarily intellectual.",
    "'Renewing of your mind' names the mechanism — what is repeatedly attended to is what does the shaping, over time and without consent.",
    "It was the third most-read verse worldwide on YouVersion in 2025, which is worth noticing given what else people were searching for that year.",
  ],
  body: [
    {
      type: "h2",
      id: "the-grammar",
      text: "The grammar is the argument",
    },
    {
      type: "p",
      text: "Do not be conformed. Be transformed. Neither is something a person performs on themselves; both describe being acted upon. That single feature does most of the work in this verse and it is almost always read straight past, because English tolerates the passive so easily that it stops registering.",
    },
    {
      type: "p",
      text: "Read with the passives visible, the claim is not that you should try harder to resist the world. It is that formation is happening either way, and the only live question is what is doing it. Nobody is standing outside the process deciding whether to enter.",
    },
    {
      type: "h2",
      id: "the-world",
      text: "What “this world” means here",
    },
    {
      type: "p",
      text: "Not the planet and not material things. The word carries the sense of an age or an era — the whole prevailing pattern of a time, its assumptions about what a good life is, what a person is for, what is worth wanting. Conformity to it is not a decision anyone makes; it is what happens to somebody immersed in an age without noticing they are in one.",
    },
    {
      type: "p",
      text: "That is why the verse has aged so well and why it is being read so heavily now. Every era has a shaping pattern and every era's members find it invisible; the specific novelty of ours is that a large part of the pattern is delivered by machines optimised to hold attention, and that the delivery is personalised, continuous and free.",
    },
    {
      type: "h2",
      id: "renewing-the-mind",
      text: "Renewing of the mind",
    },
    {
      type: "p",
      text: "The named mechanism, and it is not information. A renewed mind in this letter is one whose habitual furniture has changed — what it assumes, what it notices first, what it finds obvious. That kind of change comes from what is repeatedly attended to over long periods, which is why it can neither be achieved in an afternoon nor prevented by disagreeing with something intellectually.",
    },
    {
      type: "p",
      text: "It also explains the verse's position in the letter. It follows immediately from an instruction about bodies — present your bodies a living sacrifice — and is followed immediately by an instruction about self-assessment. The transformation described sits in the middle of an ordinary embodied week, not in a study.",
    },
  ],
  misreading: {
    claim:
      "That it is a rule about avoiding worldly entertainment — a checklist of the films, music and clothes a Christian should not be seen with.",
    correction: [
      "The checklist reading fails on the grammar. Both verbs are passive, and a checklist is a list of active refusals; the verse is not describing a set of things to avoid but a process that is occurring. A person can keep every item on such a list and be entirely conformed, and this is not a hypothetical — it is the ordinary complaint the New Testament makes about religious diligence.",
      "The checklist also aims at the wrong quantity. What forms a mind is not exposure to a thing once but repeated attention over time, which means the shaping is done less by the content anybody argues about and more by whatever is looked at for four hours a day without being noticed at all. The feed nobody has an opinion about is doing more formation than the film everybody does.",
      "Taken with the passives intact, the verse says something both more relaxed and much more demanding. Relaxed, because it is not a purity audit. Demanding, because the actual question it puts is: what has had my attention this week, and what has it made me assume is normal? Almost nobody can answer that from memory, which is itself the answer.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "Ask the verse's own question once a week: what has been forming me, and what does it now seem obvious to want? The honest version of that takes about ten minutes and a piece of paper, and it is uncomfortable in a way a checklist never is.",
    },
    {
      type: "p",
      text: "Then change an input rather than an opinion. Since formation runs on repeated attention, the lever is what gets attended to repeatedly — which is the whole argument of {why-screen-time-limits-stop-working}, and the reason a barrier that arrives automatically outperforms a resolution made once.",
      links: {
        "why-screen-time-limits-stop-working":
          "/blog/screen-time-limits-dont-work",
      },
    },
    {
      type: "p",
      text: "And if you want the same question asked about your own week rather than in general, {the-phone-and-faith-test} is twelve questions about where the attention is actually going.",
      links: { "the-phone-and-faith-test": "/quiz/phone-and-faith-test" },
    },
  ],
  faq: [
    {
      q: "What does 'conformed to this world' mean?",
      a: "The word behind 'world' means an age or era rather than the planet — the prevailing pattern of a time and its assumptions about what is worth wanting. Conformity to it is described passively: it is what happens to somebody immersed in an age, not a choice they announce.",
    },
    {
      q: "What is the renewing of the mind?",
      a: "A change in the mind's habitual furniture — what it assumes, notices first, and finds obvious — rather than an acquisition of information. It comes from what is repeatedly attended to over long periods, which is why it cannot be done in an evening.",
    },
    {
      q: "Is Romans 12:2 about avoiding secular entertainment?",
      a: "That reading struggles with the grammar: both verbs are passive, and a list of things to avoid is a list of active refusals. The verse is about what is doing the forming, and repeated unnoticed attention forms more than any single piece of content anybody argues about.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain. Most translations preserve the passive verbs; a few soften them, which is worth knowing when comparing.",
    },
  ],
  related: [
    "screen-time-limits-dont-work",
    "digital-sabbath",
    "dopamine-detox-christian",
  ],
  relatedTopics: [
    "bible-verses-about-self-control",
    "bible-verses-about-comparison",
  ],
  relatedPassages: ["philippians-4-13", "proverbs-3-5-6"],
};
